import { LinkIcon } from "@heroicons/react/24/solid";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { CounterClockwiseClockIcon } from "@radix-ui/react-icons";
import axios from "axios";
import clsx from "clsx";
import mixpanel from "mixpanel-browser";
import { observer } from "mobx-react";
import React, { useCallback, useEffect, useState } from "react";
import useDrivePicker from "react-google-drive-picker";
import { PickerConfiguration } from "react-google-drive-picker/dist/typeDefs";
import { useModalContext } from "../../contexts/modals";
import { useToastsContext } from "../../contexts/toasts";
import useStores from "../../hooks/useStores";
import { AttachmentIcon } from "../../icon-imports";
import { PricingPlanTier } from "../../models/User";
import {
  FeaturePreset,
  ResourcePreset,
  uploadFileForAttachment,
} from "../../utils/ProcessAttachments";
import OneDriveBlocks from "../OneDrive/OneDrive";
import ProPlanWall from "../ProPlanWall";
import Tooltip from "../Tooltip";
import ChatLinkInput from "./ChatLinkInput";
import { DosDontsLinkInput } from "./ChatMessageInput";
import PreviouslyUsedAttachments from "./PreviouslyUsedAttachments";
import type { FileProcessingAnswers } from "./fileProcessing";
const GoogleDriveIcon = "/imgs/blocks/google-drive.png";
const OneDriveIcon = "/imgs/blocks/onedrive.png";

// First, define a type for the uploading asset
type UploadingAsset = {
  id: string;
  name: string;
  progress: number;
};

type ChatAttachmentInputProps = {
  assetIds: string[];
  setAssetIds: React.Dispatch<React.SetStateAction<string[]>>;
  setUploadingAssets: (
    assetsOrUpdater:
      | UploadingAsset[]
      | ((current: UploadingAsset[]) => UploadingAsset[])
  ) => void;
  disabled?: boolean;
  buttonClassName?: string;
};

const MAX_NUMBER_OF_ATTACHMENTS = 10;

function ChatAttachmentInput({
  assetIds,
  setAssetIds,
  setUploadingAssets,
  disabled,
  buttonClassName,
}: ChatAttachmentInputProps) {
  const { assets, auth, auditLogs } = useStores();

  const currentPlan = auth.user?.currentPlan || PricingPlanTier.Basic;

  const hasNoAttachmentUsesLeft =
    currentPlan === PricingPlanTier.Basic &&
    auditLogs.chatFileUploadUsesLeft <= 0;

  const [openPicker] = useDrivePicker();

  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);

  const { addModal, closeModalAll } = useModalContext();

  const { addToast } = useToastsContext();

  useEffect(() => {
    if (accessToken && selectedFileId) {
      handleFilePicked(selectedFileId, accessToken);
    }
  }, [accessToken, selectedFileId]);

  const handleFileSelection = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Check if adding these files would exceed the limit
    if (assetIds.length + files.length > MAX_NUMBER_OF_ATTACHMENTS) {
      addToast(`You can only attach up to ${MAX_NUMBER_OF_ATTACHMENTS} files`, {
        type: "warning",
      });
      e.target.value = "";
      return;
    }

    // Handle multiple files
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      const uploadFile = async (
        fileBlob: Blob,
        fileName: string,
        answers: FileProcessingAnswers = {}
      ) => {
        // Add to uploading state before starting upload
        const uploadingFileId = crypto.randomUUID();
        setUploadingAssets((current) => [
          ...current,
          {
            id: uploadingFileId,
            name: fileName,
            progress: 0,
          },
        ]);

        try {
          const attachment = await uploadFileForAttachment(
            fileBlob,
            {
              name: fileName,
              feature: FeaturePreset.Chat,
              preset: ResourcePreset.Import,
              onProgress: (progress: number) => {
                const progressPercentage = Math.round(progress * 100);
                console.log("Uploading progress", progressPercentage);
                setUploadingAssets((current) =>
                  current.map((f) =>
                    f.id === uploadingFileId
                      ? { ...f, progress: progressPercentage }
                      : f
                  )
                );
              },
            },
            answers
          );

          // Remove from uploading assets once complete
          setUploadingAssets((current) =>
            current.filter((f) => f.id !== uploadingFileId)
          );

          // Add to assets store and update assetIds
          assets.add(attachment.asset);
          setAssetIds((currentAssetIds) => [
            ...currentAssetIds,
            attachment.asset.id,
          ]);

          mixpanel.track("new-file-upload-chat", {
            assetId: attachment.asset.id,
            uploadType: "DEVICE",
            processingOptions: answers,
          });
        } catch (e: any) {
          // Remove from uploading assets on error
          setUploadingAssets((current) =>
            current.filter((f) => f.id !== uploadingFileId)
          );
          // A plan-limit rejection (402 PLAN_LIMIT_EXCEEDED) opens the
          // upgrade wall instead of a generic error toast.
          if (e?.response?.data?.code === "PLAN_LIMIT_EXCEEDED") {
            addModal({
              content: <ProPlanWall featureBeingAccessed="chat" />,
              size: "md",
            });
            return;
          }
          // Show error toast to user
          const errorMessage =
            e?.response?.data?.message ||
            e?.message ||
            "Failed to upload file. Please try again.";
          addToast(errorMessage, { type: "error" });
          console.error("File upload error:", e);
        }
      };

      // PDFs no longer need processing questions: the model reads them
      // natively in chat, so upload immediately with default options.
      const isPDF = file.type === "application/pdf";
      const blob = new Blob([file], { type: file.type });
      await uploadFile(
        blob,
        file.name,
        isPDF ? { hasHandwriting: false, processImages: false } : {}
      );
    }

    // Reset the input value after processing
    e.target.value = "";
  };

  const handleOpenPicker = useCallback(() => {
    // Check if we're already at the limit
    if (assetIds.length >= MAX_NUMBER_OF_ATTACHMENTS) {
      addToast(`You can only attach up to ${MAX_NUMBER_OF_ATTACHMENTS} files`, {
        type: "warning",
      });
      return;
    }

    const clientId =
      import.meta.env.MODE === "development"
        ? import.meta.env.VITE_APP_GOOGLE_CLIENT_ID_DEV
        : import.meta.env.VITE_APP_GOOGLE_CLIENT_ID_PROD;

    const developerKey =
      import.meta.env.MODE === "development"
        ? import.meta.env.VITE_APP_GOOGLE_DEVELOPER_KEY_DEV
        : import.meta.env.VITE_APP_GOOGLE_DEVELOPER_KEY_PROD;

    const appId = import.meta.env.VITE_APP_GOOGLE_PROJECT_NUMBER;

    // Combine all supported MIME types
    const supportedMimeTypes = [
      // Standard document formats
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-powerpoint",
      // Google Workspace formats
      "application/vnd.google-apps.document",
      "application/vnd.google-apps.presentation",
      "application/vnd.google-apps.spreadsheet",
      // Email and text formats
      "message/rfc822",
      "text/html",
      "text/markdown",
      "application/vnd.ms-outlook",
      "text/x-rst",
      "application/rtf",
      "text/plain",
      "application/xml",
      // Image formats
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/bmp",
      "image/webp",
      "image/tiff",
    ].join(",");

    const config: PickerConfiguration = {
      appId,
      clientId: clientId,
      developerKey: developerKey,
      viewId: "DOCS",
      showUploadView: true,
      showUploadFolders: true,
      supportDrives: true,
      multiselect: false,
      viewMimeTypes: supportedMimeTypes,
      callbackFunction: (data: any) => {
        console.log("Data", data);

        if (data.action === "picked") {
          setSelectedFileId(data.docs[0].id);
        }
      },
    };

    // @ts-ignore
    const client = google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: ["https://www.googleapis.com/auth/drive.file"].join(" "),
      callback: (tokenResponse: any) => {
        console.log("Token response", tokenResponse);

        config.token = tokenResponse.access_token;
        setAccessToken(tokenResponse.access_token);
        openPicker(config);
      },
    });

    client.requestAccessToken();
  }, [openPicker, assetIds.length]);

  const handleFilePicked = async (fileId: string, accessToken: string) => {
    try {
      const metadataResponse = await axios.get(
        `https://www.googleapis.com/drive/v3/files/${fileId}?fields=name,mimeType`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      let { name, mimeType } = metadataResponse.data;
      let fileContent;
      let exportMimeType: string | null = null;

      // Check if it's a Google-specific format
      if (mimeType.includes("application/vnd.google-apps")) {
        // For Google Docs
        if (mimeType === "application/vnd.google-apps.document") {
          exportMimeType = "application/pdf";
          name = name.endsWith(".pdf") ? name : `${name}.pdf`;
        }
        // For Google Sheets
        else if (mimeType === "application/vnd.google-apps.spreadsheet") {
          exportMimeType =
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
          name = name.endsWith(".xlsx") ? name : `${name}.xlsx`;
        }
        // For Google Slides
        else if (mimeType === "application/vnd.google-apps.presentation") {
          exportMimeType = "application/pdf";
          name = name.endsWith(".pdf") ? name : `${name}.pdf`;
        }
        // For other Google formats, default to PDF
        else {
          exportMimeType = "application/pdf";
          name = name.endsWith(".pdf") ? name : `${name}.pdf`;
        }

        console.log("Exporting as", exportMimeType);
        console.log("New name", name);

        // Use the export endpoint for Google-specific formats
        const exportResponse = await axios.get(
          `https://www.googleapis.com/drive/v3/files/${fileId}/export?mimeType=${exportMimeType}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            responseType: "arraybuffer",
          }
        );
        fileContent = exportResponse.data;
      } else {
        // For non-Google formats, use the regular download
        const contentResponse = await axios.get(
          `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            responseType: "arraybuffer",
          }
        );
        fileContent = contentResponse.data;
      }

      const blob = new Blob([fileContent], {
        type: exportMimeType || mimeType,
      });

      const isPDF = (exportMimeType || mimeType) === "application/pdf";

      const uploadFile = async (
        fileBlob: Blob,
        fileName: string,
        answers: FileProcessingAnswers = {}
      ) => {
        const uploadingFileId = crypto.randomUUID();
        setUploadingAssets((current) => [
          ...current,
          {
            id: uploadingFileId,
            name: fileName,
            progress: 0,
          },
        ]);

        try {
          const attachment = await uploadFileForAttachment(
            fileBlob,
            {
              name: fileName,
              feature: FeaturePreset.Chat,
              preset: ResourcePreset.Import,
              onProgress: (progress: number) => {
                const progressPercentage = Math.round(progress * 100);
                console.log("Uploading progress", progressPercentage);
                setUploadingAssets((current) =>
                  current.map((f) =>
                    f.id === uploadingFileId
                      ? { ...f, progress: progressPercentage }
                      : f
                  )
                );
              },
            },
            answers
          );

          setUploadingAssets((current) =>
            current.filter((f) => f.id !== uploadingFileId)
          );

          assets.add(attachment.asset);
          setAssetIds((currentAssetIds) => [
            ...currentAssetIds,
            attachment.asset.id,
          ]);

          mixpanel.track("new-file-upload-chat", {
            assetId: attachment.asset.id,
            uploadType: "GOOGLE_DRIVE",
            processingOptions: answers,
          });

          setSelectedFileId(null);
        } catch (e: any) {
          setUploadingAssets((current) =>
            current.filter((f) => f.id !== uploadingFileId)
          );
          // Show error toast to user
          const errorMessage =
            e?.response?.data?.message ||
            e?.message ||
            "Failed to upload file. Please try again.";
          addToast(errorMessage, { type: "error" });
          console.error("Google Drive file upload error:", e);
        }
      };

      await uploadFile(
        blob,
        name,
        isPDF ? { hasHandwriting: false, processImages: false } : {}
      );
    } catch (error: any) {
      console.error("Error downloading file from Google Drive:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to download file from Google Drive. Please try again.";
      addToast(errorMessage, { type: "error" });
    }
  };

  // Basic plan users with no attachment uses left see the Pro plan wall
  // instead of the attachment menu.
  if (hasNoAttachmentUsesLeft) {
    return (
      <Tooltip
        content="Upgrade to Pro for more attachments"
        sideOffset={15}
        delayDuration={0}
        asChild={true}
      >
        <button
          id="ask-alayna-attachment-input"
          type="button"
          className={clsx(
            "flex items-center rounded-md border-solid transition-colors",
            disabled
              ? "cursor-not-allowed opacity-50"
              : "border-gray-700 hover:bg-gray-100 active:bg-gray-200",
            buttonClassName
          )}
          aria-label="Upgrade to Pro for more attachments"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (disabled) return;
            addModal({
              content: <ProPlanWall featureBeingAccessed="chat" />,
              size: "md",
            });
          }}
        >
          <AttachmentIcon
            className={clsx(
              "h-7 w-7",
              disabled ? "text-gray-400" : "text-gray-500"
            )}
          />
        </button>
      </Tooltip>
    );
  }

  return (
    <>
      <input
        type="file"
        id="fileInput"
        multiple
        style={{ display: "none" }}
        onChange={handleFileSelection}
        disabled={disabled}
      />

      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <Tooltip
            content="Attach a file or link as reference."
            sideOffset={15}
            delayDuration={0}
            asChild={true}
          >
            <button
              id="ask-alayna-attachment-input"
              className={clsx(
                "flex items-center rounded-md border-solid transition-colors",
                disabled
                  ? "cursor-not-allowed opacity-50"
                  : "border-gray-700 hover:bg-gray-100 active:bg-gray-200",
                buttonClassName
              )}
              aria-label="Add attachment"
            >
              <AttachmentIcon
                className={clsx(
                  "h-7 w-7",
                  disabled ? "text-gray-400" : "text-gray-500"
                )}
              />
            </button>
          </Tooltip>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className={clsx(
              "DropdownMenuContent z-100 rounded-lg border-[1px] border-gray-100 bg-white text-[15px] shadow-lg",
              disabled ? "pointer-events-none opacity-50" : "text-gray-700"
            )}
            sideOffset={5}
          >
            {/* header */}
            <DropdownMenu.Label className="DropdownMenuLabel">
              <div className="text-gray-900 border-b-[1px] border-gray-100 py-2 pl-2 text-sm font-medium">
                Insert Attachment
              </div>
            </DropdownMenu.Label>
            <DropdownMenu.Item
              onSelect={() => {
                if (disabled) return;
                document.getElementById("fileInput")?.click();
              }}
              className={clsx(
                "cursor-pointer border-b-[1px] border-gray-100 px-3 py-2 pr-5",
                disabled
                  ? "cursor-not-allowed opacity-50"
                  : "hover:bg-gray-50 hover:outline-none"
              )}
            >
              <label
                htmlFor="fileInput"
                className="h-full w-full cursor-pointer"
              >
                <Tooltip content="Import file from your device" side="right">
                  <div className="flex">
                    <div className="w-9">
                      <AttachmentIcon className="text-gray-700 h-6 w-7" />
                    </div>
                    File Upload
                  </div>
                </Tooltip>
              </label>
            </DropdownMenu.Item>
            <DropdownMenu.Item
              onSelect={() => {
                if (disabled) return;
                addModal({
                  content: (
                    <div className="flex flex-col items-center">
                      <div className="max-w-[500px]">
                        <ChatLinkInput setAssetIds={setAssetIds} />

                        {<DosDontsLinkInput />}
                      </div>
                    </div>
                  ),
                  size: "md",
                  title: "Attach Website or Youtube Link",
                });
              }}
              className={clsx(
                "cursor-pointer border-b-[1px] border-gray-100 px-3 py-2 pr-5",
                disabled
                  ? "cursor-not-allowed opacity-50"
                  : "hover:bg-gray-50 hover:outline-none"
              )}
            >
              <Tooltip
                content="Provide Website or Youtube link as reference to Alayna"
                side="right"
              >
                <div className="flex cursor-pointer">
                  <div className="w-9">
                    <LinkIcon className="text-gray-700  h-6 w-7" />
                  </div>
                  Web/Youtube Link
                </div>
              </Tooltip>
            </DropdownMenu.Item>
            <DropdownMenu.Item
              onSelect={(e) => {
                if (disabled) return;
                e.stopPropagation();
                // We need to open the Google Drive Picker API
                handleOpenPicker();
              }}
              className={clsx(
                "cursor-pointer border-b-[1px] border-gray-100 px-3 py-2 pr-5",
                disabled
                  ? "cursor-not-allowed opacity-50"
                  : "hover:bg-gray-50 hover:outline-none"
              )}
            >
              <Tooltip content="Import file from Google Drive" side="right">
                <div className="flex">
                  <div className="w-9">
                    <img
                      loading="lazy"
                      src={GoogleDriveIcon}
                      className="text-gray-700 mt-[0.3em] ml-[0.35em] h-[1.2em] w-[1.2em] cursor-pointer"
                    />
                  </div>
                  Google Drive
                </div>
              </Tooltip>
            </DropdownMenu.Item>
            <DropdownMenu.Item
              onSelect={(e) => {
                if (disabled) return;
                e.stopPropagation();
                // Check OneDrive limit before opening modal
                if (assetIds.length >= MAX_NUMBER_OF_ATTACHMENTS) {
                  addToast(
                    `You can only attach up to ${MAX_NUMBER_OF_ATTACHMENTS} files`,
                    {
                      type: "warning",
                    }
                  );
                  return;
                }
                addModal({
                  content: (
                    <OneDriveBlocks
                      setAttachment={(attachment: any) => {
                        // Double check limit before adding attachment
                        if (assetIds.length >= MAX_NUMBER_OF_ATTACHMENTS) {
                          addToast(
                            `You can only attach up to ${MAX_NUMBER_OF_ATTACHMENTS} files`,
                            {
                              type: "warning",
                            }
                          );
                          return;
                        }
                        console.log("Set Attachment from OneDrive", attachment);
                        assets.add(attachment.asset);
                        setAssetIds((currentAssetIds) => [
                          ...currentAssetIds,
                          attachment.asset.id,
                        ]);
                        mixpanel.track("new-file-upload-chat", {
                          assetId: attachment.asset.id,
                          uploadType: "ONE_DRIVE",
                        });
                      }}
                      feature={FeaturePreset.Chat}
                    />
                  ),
                  size: "md",
                });
              }}
              className={clsx(
                "cursor-pointer border-b-[1px] border-gray-100 px-3 py-2 pr-5",
                disabled
                  ? "cursor-not-allowed opacity-50"
                  : "hover:bg-gray-50 hover:outline-none"
              )}
            >
              <Tooltip
                content="Import file from Microsoft OneDrive"
                side="right"
              >
                <div className="flex">
                  <div className="w-9">
                    <img
                      loading="lazy"
                      src={OneDriveIcon}
                      className="text-gray-700 mt-[0.3em] ml-[0.3em] h-[0.85em] w-[1.4em] cursor-pointer"
                    />
                  </div>
                  Microsoft OneDrive
                </div>
              </Tooltip>
            </DropdownMenu.Item>
            <DropdownMenu.Item
              onSelect={() => {
                if (disabled) return;
                addModal({
                  content: (
                    <PreviouslyUsedAttachments
                      onSelect={(asset) => {
                        assets.add(asset);
                        setAssetIds((currentAssetIds) => [
                          ...currentAssetIds,
                          asset.id,
                        ]);
                        closeModalAll();
                      }}
                      onClose={() => closeModalAll()}
                    />
                  ),
                  size: "md",
                });
              }}
              className={clsx(
                "cursor-pointer px-3 py-2 pr-5",
                disabled
                  ? "cursor-not-allowed opacity-50"
                  : "hover:bg-gray-50 hover:outline-none"
              )}
            >
              <Tooltip
                content="Select from previously used attachments"
                side="right"
              >
                <div className="flex">
                  <div className="w-9">
                    <CounterClockwiseClockIcon className="text-gray-700 h-6 w-6 ml-1" />
                  </div>
                  Previously Used
                </div>
              </Tooltip>
            </DropdownMenu.Item>
            <DropdownMenu.Arrow className="DropdownMenuArrow" />
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </>
  );
}

export default observer(ChatAttachmentInput);
